import type {
  Flashcard,
  FlashcardCollection,
  FlashcardDeck,
  FlashcardDeckOptions,
  FlashcardMedia,
  FlashcardNote,
  FlashcardNoteType,
  FlashcardReview,
  FlashcardTemplate,
  UploadResponse,
} from "@japa-reader/contracts";
import { cloudIsConfigured } from "../auth";
import { apiFetch } from "./api";
import {
  FLASHCARD_CARDS_STORE,
  FLASHCARD_DECKS_STORE,
  FLASHCARD_MEDIA_STORE,
  FLASHCARD_NOTES_STORE,
  FLASHCARD_NOTE_TYPES_STORE,
  FLASHCARD_REVIEWS_STORE,
  openDatabase,
  requestResult,
  transactionDone,
} from "./library";

export const DEFAULT_DECK_ID = "deck-default";
export const BASIC_NOTE_TYPE_ID = "note-type-basic";
export const BASIC_REVERSED_NOTE_TYPE_ID = "note-type-basic-reversed";
export const CLOZE_NOTE_TYPE_ID = "note-type-cloze";

export const DEFAULT_DECK_OPTIONS: FlashcardDeckOptions = {
  scheduler: "fsrs",
  newCardsPerDay: 20,
  maximumReviewsPerDay: 200,
  learningStepsMinutes: [1, 10],
  relearningStepsMinutes: [10],
  graduatingIntervalDays: 1,
  easyIntervalDays: 4,
  maximumIntervalDays: 36_500,
  desiredRetention: 0.9,
  fsrsParameters: [],
  startingEase: 2.5,
  easyBonus: 1.3,
  intervalModifier: 1,
  hardInterval: 1.2,
  newInterval: 0,
  leechThreshold: 8,
  leechAction: "suspend",
  newOrder: "added",
  reviewOrder: "due",
  newReviewMix: "mix",
  buryNewSiblings: true,
  buryReviewSiblings: true,
  showAnswerTimer: false,
  maximumAnswerSeconds: 60,
  autoPlayAudio: true,
  autoAdvanceSeconds: 0,
};

export async function loadFlashcardCollection(): Promise<FlashcardCollection> {
  const collection = cloudIsConfigured()
    ? await apiFetch<FlashcardCollection>("/flashcards")
    : await loadLocalCollection();
  return ensureDefaults(collection);
}

export async function saveFlashcardDeck(deck: FlashcardDeck) {
  if (cloudIsConfigured()) {
    await apiFetch(`/flashcards/decks/${encodeURIComponent(deck.id)}`, { method: "PUT", body: JSON.stringify(deck) });
    return;
  }
  await putLocal(FLASHCARD_DECKS_STORE, deck);
}

export async function deleteFlashcardDeck(deckId: string) {
  if (cloudIsConfigured()) {
    await apiFetch(`/flashcards/decks/${encodeURIComponent(deckId)}`, { method: "DELETE" });
    return;
  }
  const collection = await loadLocalCollection();
  const deckIds = descendantDeckIds(deckId, collection.decks);
  const notes = collection.notes.filter((note) => deckIds.has(note.deckId));
  const noteIds = new Set(notes.map((note) => note.id));
  const cards = collection.cards.filter((card) => deckIds.has(card.deckId) || noteIds.has(card.noteId));
  const cardIds = new Set(cards.map((card) => card.id));
  const reviews = collection.reviews.filter((review) => cardIds.has(review.cardId));
  const database = await openDatabase();
  const transaction = database.transaction([
    FLASHCARD_DECKS_STORE,
    FLASHCARD_NOTES_STORE,
    FLASHCARD_CARDS_STORE,
    FLASHCARD_REVIEWS_STORE,
  ], "readwrite");
  for (const id of deckIds) transaction.objectStore(FLASHCARD_DECKS_STORE).delete(id);
  for (const note of notes) transaction.objectStore(FLASHCARD_NOTES_STORE).delete(note.id);
  for (const card of cards) transaction.objectStore(FLASHCARD_CARDS_STORE).delete(card.id);
  for (const review of reviews) transaction.objectStore(FLASHCARD_REVIEWS_STORE).delete(review.id);
  await transactionDone(transaction);
}

export async function saveFlashcardNoteType(noteType: FlashcardNoteType) {
  if (cloudIsConfigured()) {
    await apiFetch(`/flashcards/note-types/${encodeURIComponent(noteType.id)}`, { method: "PUT", body: JSON.stringify(noteType) });
    return;
  }
  await putLocal(FLASHCARD_NOTE_TYPES_STORE, noteType);
}

export async function deleteFlashcardNoteType(noteTypeId: string) {
  if (cloudIsConfigured()) {
    await apiFetch(`/flashcards/note-types/${encodeURIComponent(noteTypeId)}`, { method: "DELETE" });
    return;
  }
  const collection = await loadLocalCollection();
  if (collection.notes.some((note) => note.noteTypeId === noteTypeId)) {
    throw new Error("Mova ou exclua as notas deste tipo antes de removê-lo.");
  }
  await deleteLocal(FLASHCARD_NOTE_TYPES_STORE, noteTypeId);
}

export async function saveFlashcardNote(note: FlashcardNote, cards: Flashcard[], removedCardIds: string[] = []) {
  if (cloudIsConfigured()) {
    await apiFetch(`/flashcards/notes/${encodeURIComponent(note.id)}`, { method: "PUT", body: JSON.stringify(note) });
    await Promise.all([
      ...cards.map((card) => apiFetch(`/flashcards/cards/${encodeURIComponent(card.id)}`, { method: "PUT", body: JSON.stringify(card) })),
      ...removedCardIds.map((id) => apiFetch(`/flashcards/cards/${encodeURIComponent(id)}`, { method: "DELETE" })),
    ]);
    return;
  }
  const removedIds = new Set(removedCardIds);
  const removedReviewIds = removedIds.size
    ? (await loadLocalCollection()).reviews.filter((review) => removedIds.has(review.cardId)).map((review) => review.id)
    : [];
  const database = await openDatabase();
  const transaction = database.transaction([FLASHCARD_NOTES_STORE, FLASHCARD_CARDS_STORE, FLASHCARD_REVIEWS_STORE], "readwrite");
  transaction.objectStore(FLASHCARD_NOTES_STORE).put(note);
  for (const card of cards) transaction.objectStore(FLASHCARD_CARDS_STORE).put(card);
  for (const id of removedCardIds) transaction.objectStore(FLASHCARD_CARDS_STORE).delete(id);
  for (const id of removedReviewIds) transaction.objectStore(FLASHCARD_REVIEWS_STORE).delete(id);
  await transactionDone(transaction);
}

export async function deleteFlashcardNote(noteId: string) {
  if (cloudIsConfigured()) {
    await apiFetch(`/flashcards/notes/${encodeURIComponent(noteId)}`, { method: "DELETE" });
    return;
  }
  const collection = await loadLocalCollection();
  const cards = collection.cards.filter((card) => card.noteId === noteId);
  const cardIds = new Set(cards.map((card) => card.id));
  const reviews = collection.reviews.filter((review) => cardIds.has(review.cardId));
  const database = await openDatabase();
  const transaction = database.transaction([FLASHCARD_NOTES_STORE, FLASHCARD_CARDS_STORE, FLASHCARD_REVIEWS_STORE], "readwrite");
  transaction.objectStore(FLASHCARD_NOTES_STORE).delete(noteId);
  for (const card of cards) transaction.objectStore(FLASHCARD_CARDS_STORE).delete(card.id);
  for (const review of reviews) transaction.objectStore(FLASHCARD_REVIEWS_STORE).delete(review.id);
  await transactionDone(transaction);
}

export async function saveFlashcardCard(card: Flashcard) {
  if (cloudIsConfigured()) {
    await apiFetch(`/flashcards/cards/${encodeURIComponent(card.id)}`, { method: "PUT", body: JSON.stringify(card) });
    return;
  }
  await putLocal(FLASHCARD_CARDS_STORE, card);
}

export async function recordFlashcardReview(card: Flashcard, review: FlashcardReview) {
  if (cloudIsConfigured()) {
    await apiFetch(`/flashcards/cards/${encodeURIComponent(card.id)}/review`, {
      method: "POST",
      body: JSON.stringify({ card, review }),
    });
    return;
  }
  const database = await openDatabase();
  const transaction = database.transaction([FLASHCARD_CARDS_STORE, FLASHCARD_REVIEWS_STORE], "readwrite");
  transaction.objectStore(FLASHCARD_CARDS_STORE).put(card);
  transaction.objectStore(FLASHCARD_REVIEWS_STORE).put(review);
  await transactionDone(transaction);
}

export async function deleteFlashcardReview(reviewId: string) {
  if (cloudIsConfigured()) {
    await apiFetch(`/flashcards/reviews/${encodeURIComponent(reviewId)}`, { method: "DELETE" });
    return;
  }
  await deleteLocal(FLASHCARD_REVIEWS_STORE, reviewId);
}

export async function saveFlashcardMedia(file: File): Promise<FlashcardMedia> {
  if (file.size <= 0 || file.size > 50 * 1024 * 1024) throw new Error("A mídia precisa ter no máximo 50 MB.");
  const now = Date.now();
  const id = `media-${crypto.randomUUID()}`;
  const media: FlashcardMedia = {
    id,
    name: file.name.normalize("NFKC").slice(0, 240) || "media",
    contentType: file.type || "application/octet-stream",
    size: file.size,
    blob: file,
    url: URL.createObjectURL(file),
    createdAt: now,
    updatedAt: now,
  };
  if (cloudIsConfigured()) {
    const upload = await apiFetch<UploadResponse>("/uploads", {
      method: "POST",
      body: JSON.stringify({ kind: "flashcard-media", fileName: media.name, contentType: media.contentType, size: media.size }),
    });
    const response = await fetch(upload.uploadUrl, { method: "PUT", headers: { "Content-Type": media.contentType }, body: file });
    if (!response.ok) throw new Error(`A mídia não pôde ser enviada ao armazenamento seguro (S3 ${response.status}).`);
    const cloudMedia = { ...media, blob: null, url: undefined, storageKey: upload.key };
    const saved = await apiFetch<{ media: FlashcardMedia }>(`/flashcards/media/${encodeURIComponent(id)}`, { method: "PUT", body: JSON.stringify(cloudMedia) });
    if (media.url) URL.revokeObjectURL(media.url);
    return saved.media;
  }
  await putLocal(FLASHCARD_MEDIA_STORE, { ...media, url: undefined });
  return media;
}

export async function deleteFlashcardMedia(media: FlashcardMedia) {
  if (media.url?.startsWith("blob:")) URL.revokeObjectURL(media.url);
  if (cloudIsConfigured()) {
    await apiFetch(`/flashcards/media/${encodeURIComponent(media.id)}`, { method: "DELETE" });
    return;
  }
  await deleteLocal(FLASHCARD_MEDIA_STORE, media.id);
}

export function newFlashcardDeck(name: string, parentId: string | null = null): FlashcardDeck {
  const now = Date.now();
  return {
    id: makeFlashcardId("deck"),
    name: name.trim(),
    parentId,
    description: "",
    kind: "standard",
    filter: "",
    filterLimit: 100,
    filterOrder: "due",
    rescheduleFilteredCards: true,
    optionsPresetName: "Padrão",
    options: { ...DEFAULT_DECK_OPTIONS },
    createdAt: now,
    updatedAt: now,
  };
}

export function newFlashcardNote(deckId: string, noteTypeId = BASIC_NOTE_TYPE_ID): FlashcardNote {
  const now = Date.now();
  return {
    id: makeFlashcardId("note"),
    deckId,
    noteTypeId,
    fields: {},
    tags: [],
    createdAt: now,
    updatedAt: now,
  };
}

export function reconcileNoteCards(note: FlashcardNote, noteType: FlashcardNoteType, existing: Flashcard[]): {
  cards: Flashcard[];
  removedCardIds: string[];
} {
  const now = Date.now();
  const candidates = generatedCardKeys(note, noteType);
  const existingByKey = new Map(existing.map((card) => [cardKey(card.templateId, card.clozeOrdinal), card]));
  const cards = candidates.map(({ template, clozeOrdinal }, index) => {
    const old = existingByKey.get(cardKey(template.id, clozeOrdinal));
    const deckId = template.deckOverrideId || note.deckId;
    return old ? { ...old, deckId, templateOrdinal: template.ordinal, updatedAt: now } : emptyFlashcard({
      id: `card:${note.id}:${template.id}:${clozeOrdinal ?? 0}`,
      noteId: note.id,
      deckId,
      template,
      clozeOrdinal,
      position: now + index,
    });
  });
  const nextIds = new Set(cards.map((card) => card.id));
  return { cards, removedCardIds: existing.filter((card) => !nextIds.has(card.id)).map((card) => card.id) };
}

export function emptyFlashcard(input: {
  id: string;
  noteId: string;
  deckId: string;
  template: FlashcardTemplate;
  clozeOrdinal: number | null;
  position: number;
}): Flashcard {
  const now = Date.now();
  return {
    id: input.id,
    noteId: input.noteId,
    deckId: input.deckId,
    templateId: input.template.id,
    templateOrdinal: input.template.ordinal,
    clozeOrdinal: input.clozeOrdinal,
    state: "new",
    due: now,
    stability: 0,
    difficulty: 0,
    elapsedDays: 0,
    scheduledDays: 0,
    learningSteps: 0,
    reps: 0,
    lapses: 0,
    lastReview: null,
    suspended: false,
    buriedUntil: null,
    flag: 0,
    position: input.position,
    createdAt: now,
    updatedAt: now,
  };
}

export function makeFlashcardId(prefix: "deck" | "note" | "note-type" | "review") {
  return `${prefix}-${crypto.randomUUID()}`;
}

function generatedCardKeys(note: FlashcardNote, noteType: FlashcardNoteType) {
  if (noteType.kind === "cloze") {
    const ordinals = new Set<number>();
    for (const value of Object.values(note.fields)) {
      for (const match of value.matchAll(/\{\{c(\d+)::[\s\S]*?\}\}/gi)) ordinals.add(Number(match[1]));
    }
    const template = noteType.templates[0];
    return template ? [...ordinals].sort((a, b) => a - b).map((clozeOrdinal) => ({ template, clozeOrdinal })) : [];
  }
  return noteType.templates
    .filter((template) => templateFrontHasContent(template.frontTemplate, note.fields))
    .map((template) => ({ template, clozeOrdinal: null }));
}

function templateFrontHasContent(template: string, fields: Record<string, string>) {
  const fieldNames = [...template.matchAll(/\{\{(?:[^}:]+:)?([^}#/]+)\}\}/g)].map((match) => match[1].trim());
  return fieldNames.length ? fieldNames.some((name) => Boolean(fields[name]?.trim())) : Boolean(template.trim());
}

function cardKey(templateId: string, clozeOrdinal: number | null) {
  return `${templateId}:${clozeOrdinal ?? 0}`;
}

async function ensureDefaults(collection: FlashcardCollection) {
  const next = { ...collection, decks: [...collection.decks], noteTypes: [...collection.noteTypes], media: [...(collection.media || [])] };
  const saves: Promise<void>[] = [];
  if (!next.decks.length) {
    const deck = defaultDeck();
    next.decks.push(deck);
    saves.push(saveFlashcardDeck(deck));
  }
  const builtIns = defaultNoteTypes();
  for (const builtIn of builtIns) {
    if (!next.noteTypes.some((noteType) => noteType.id === builtIn.id)) {
      next.noteTypes.push(builtIn);
      saves.push(saveFlashcardNoteType(builtIn));
    }
  }
  await Promise.all(saves);
  return next;
}

function defaultDeck(): FlashcardDeck {
  const now = Date.now();
  return {
    id: DEFAULT_DECK_ID,
    name: "Padrão",
    parentId: null,
    description: "Seu primeiro baralho de flashcards.",
    kind: "standard",
    filter: "",
    filterLimit: 100,
    filterOrder: "due",
    rescheduleFilteredCards: true,
    optionsPresetName: "Padrão",
    options: { ...DEFAULT_DECK_OPTIONS },
    createdAt: now,
    updatedAt: now,
  };
}

function defaultNoteTypes(): FlashcardNoteType[] {
  const now = Date.now();
  const css = ".card { font-family: Inter, sans-serif; font-size: 28px; text-align: center; color: #f7f3eb; } .answer { color: #c9a8ff; }";
  const field = (id: string, name: string, ordinal: number) => ({ id, name, ordinal, font: "Inter", fontSize: 20, rtl: false, sticky: false });
  const template = (id: string, name: string, ordinal: number, frontTemplate: string, backTemplate: string): FlashcardTemplate => ({
    id, name, ordinal, frontTemplate, backTemplate, css, deckOverrideId: null,
  });
  return [
    {
      id: BASIC_NOTE_TYPE_ID,
      name: "Básico",
      kind: "standard",
      fields: [field("field-front", "Frente", 0), field("field-back", "Verso", 1)],
      templates: [template("template-forward", "Cartão 1", 0, "{{Frente}}", "{{FrontSide}}<hr><div class=\"answer\">{{Verso}}</div>")],
      createdAt: now,
      updatedAt: now,
    },
    {
      id: BASIC_REVERSED_NOTE_TYPE_ID,
      name: "Básico (e cartão invertido)",
      kind: "standard",
      fields: [field("field-front-rev", "Frente", 0), field("field-back-rev", "Verso", 1)],
      templates: [
        template("template-forward-rev", "Frente → Verso", 0, "{{Frente}}", "{{FrontSide}}<hr><div class=\"answer\">{{Verso}}</div>"),
        template("template-backward-rev", "Verso → Frente", 1, "{{Verso}}", "{{FrontSide}}<hr><div class=\"answer\">{{Frente}}</div>"),
      ],
      createdAt: now,
      updatedAt: now,
    },
    {
      id: CLOZE_NOTE_TYPE_ID,
      name: "Omissão de palavras (Cloze)",
      kind: "cloze",
      fields: [field("field-text", "Texto", 0), field("field-extra", "Extra", 1)],
      templates: [template("template-cloze", "Cloze", 0, "{{cloze:Texto}}", "{{cloze:Texto}}<hr><div class=\"answer\">{{Extra}}</div>")],
      createdAt: now,
      updatedAt: now,
    },
  ];
}

async function loadLocalCollection(): Promise<FlashcardCollection> {
  const database = await openDatabase();
  const stores = [FLASHCARD_DECKS_STORE, FLASHCARD_NOTE_TYPES_STORE, FLASHCARD_NOTES_STORE, FLASHCARD_CARDS_STORE, FLASHCARD_REVIEWS_STORE, FLASHCARD_MEDIA_STORE];
  const transaction = database.transaction(stores, "readonly");
  const [decks, noteTypes, notes, cards, reviews, storedMedia] = await Promise.all([
    requestResult(transaction.objectStore(FLASHCARD_DECKS_STORE).getAll() as IDBRequest<FlashcardDeck[]>),
    requestResult(transaction.objectStore(FLASHCARD_NOTE_TYPES_STORE).getAll() as IDBRequest<FlashcardNoteType[]>),
    requestResult(transaction.objectStore(FLASHCARD_NOTES_STORE).getAll() as IDBRequest<FlashcardNote[]>),
    requestResult(transaction.objectStore(FLASHCARD_CARDS_STORE).getAll() as IDBRequest<Flashcard[]>),
    requestResult(transaction.objectStore(FLASHCARD_REVIEWS_STORE).getAll() as IDBRequest<FlashcardReview[]>),
    requestResult(transaction.objectStore(FLASHCARD_MEDIA_STORE).getAll() as IDBRequest<FlashcardMedia[]>),
  ]);
  await transactionDone(transaction);
  const media = storedMedia.map((item) => ({ ...item, url: item.blob ? URL.createObjectURL(item.blob) : item.url }));
  return { decks, noteTypes, notes, cards, reviews, media };
}

async function putLocal(storeName: string, value: unknown) {
  const database = await openDatabase();
  const transaction = database.transaction(storeName, "readwrite");
  transaction.objectStore(storeName).put(value);
  await transactionDone(transaction);
}

async function deleteLocal(storeName: string, id: string) {
  const database = await openDatabase();
  const transaction = database.transaction(storeName, "readwrite");
  transaction.objectStore(storeName).delete(id);
  await transactionDone(transaction);
}

function descendantDeckIds(rootId: string, decks: FlashcardDeck[]) {
  const ids = new Set([rootId]);
  let changed = true;
  while (changed) {
    changed = false;
    for (const deck of decks) {
      if (deck.parentId && ids.has(deck.parentId) && !ids.has(deck.id)) {
        ids.add(deck.id);
        changed = true;
      }
    }
  }
  return ids;
}
